/**
 * Approval chains — Step 47.
 *
 * Shared because the SAME question is asked in two places: the engine decides
 * whether a stage has resolved, and the UI has to show an approver why the
 * thing they just approved is still waiting. Two implementations of "has this
 * stage passed?" would disagree on exactly the cases that matter.
 *
 * Pure functions, no database, written and tested BEFORE the tables — the
 * same discipline the plan sets for `slaMath`: *"business-hours arithmetic …
 * is pure functions, no database needed. Write it, prove it, then build the
 * schema on something already known to be correct."*
 */

/** How many of a stage's approvers have to say yes. */
/**
 * How many of a stage's approvers have to agree — Step 196.
 *
 * `majority` is Freshservice's third option ("The condition on who has to
 * approve (any one, everyone or majority) can be selected from the drop-down
 * 'To be approved by'") and it is the one a real CAB usually wants: `any` lets
 * one person wave through a change five people were asked about, and `all`
 * lets one person on annual leave stop it.
 */
export type ApprovalMode = 'any' | 'all' | 'majority';

/**
 * One spelling of each mode, because there were six.
 *
 * Step 196. Every surface rendered the mode as a ternary — `mode === 'all' ?
 * 'all' : 'any one'` — so adding a third mode silently labelled every majority
 * stage "any one", in the admin list, the chain summary, the approvals queue
 * and the inbox. A binary written four ways is a third option waiting to be
 * mislabelled.
 */
export const APPROVAL_MODE_SHORT: Record<ApprovalMode, string> = {
  any: 'any one',
  majority: 'a majority',
  all: 'all',
};

export const APPROVAL_MODE_LONG: Record<ApprovalMode, string> = {
  any: 'any one approver',
  majority: 'a majority of approvers',
  all: 'everyone must approve',
};

/** How many approvals a stage of this size needs to reach a majority. */
export function majorityNeeded(total: number): number {
  return Math.floor(total / 2) + 1;
}

export type ResponseStatus = 'pending' | 'approved' | 'rejected';

/** `waiting` = a later stage that has not opened yet. The distinction from
 *  `pending` is the whole point of a chain: nobody should be asked for a
 *  decision that a previous stage may still make unnecessary. */
export type StageStatus = 'waiting' | 'pending' | 'approved' | 'rejected' | 'cancelled';

export interface StageInput {
  stage: number;
  mode: ApprovalMode;
  status: StageStatus;
  responses: { userId: string; status: ResponseStatus }[];
}

/**
 * Has this stage resolved, given who has answered?
 *
 * The `any` rejection rule is the subtle one. One person saying no does NOT
 * reject an `any` stage — the point of "any" is that one yes is enough, so a
 * single no only matters once nobody is left who could still say yes. Treating
 * the first rejection as fatal would make "any" behave like "all" in reverse,
 * and would let one unavailable approver block a request the rest were happy
 * with.
 *
 * `all` is the mirror and is NOT symmetric: there, one no is fatal, because
 * unanimity is already impossible.
 */
export function stageOutcome(mode: ApprovalMode, responses: { status: ResponseStatus }[]): ResponseStatus {
  if (responses.length === 0) return 'pending';
  const approved = responses.filter((r) => r.status === 'approved').length;
  const rejected = responses.filter((r) => r.status === 'rejected').length;
  if (mode === 'any') {
    if (approved > 0) return 'approved';
    if (rejected === responses.length) return 'rejected';
    return 'pending';
  }
  if (mode === 'majority') {
    const needed = majorityNeeded(responses.length);
    if (approved >= needed) return 'approved';
    // Settled the moment a majority is arithmetically out of reach, rather
    // than waiting for everyone. Three of five having said no is a decision;
    // holding the change open for the other two to answer a question that can
    // no longer change the outcome is theatre.
    if (responses.length - rejected < needed) return 'rejected';
    return 'pending';
  }
  if (rejected > 0) return 'rejected';
  if (approved === responses.length) return 'approved';
  return 'pending';
}

export interface ChainVerdict {
  /** What the chain as a whole now says. */
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  /** The stage that should be OPEN — 1-based, or null when nothing is left to
   *  ask. Whoever applies this opens exactly this one. */
  openStage: number | null;
  /** Stages whose stored status is now wrong, and what it should be. Returned
   *  rather than applied so the caller owns the transaction. */
  changes: { stage: number; status: StageStatus }[];
}

/**
 * Walk a chain and say where it stands.
 *
 * Stage N+1 opens only when N is approved. A rejection anywhere ends the whole
 * chain: the later stages are not "still waiting", they are never going to be
 * asked, and leaving them as `waiting` would keep the request alive in every
 * "what is outstanding?" query forever.
 */
export function evaluateChain(stagesIn: StageInput[]): ChainVerdict {
  const stages = [...stagesIn].sort((a, b) => a.stage - b.stage);
  const changes: { stage: number; status: StageStatus }[] = [];
  const want = (s: StageInput, status: StageStatus) => {
    if (s.status !== status) changes.push({ stage: s.stage, status });
  };

  /**
   * A cancelled chain stays cancelled — but a REJECTION outranks the
   * cancellations it caused.
   *
   * Rejecting stage 1 cancels stages 2 and 3, which is right. Re-reading that
   * chain then found a cancelled stage and reported the whole thing as
   * "cancelled", losing the fact that somebody actually said no. Those are
   * different answers to different questions — withdrawn by the requester
   * versus refused by an approver — and only one of them is a decision.
   *
   * Caught against the live database, not by the unit tests: they never
   * re-evaluated a chain AFTER persisting a rejection, which is exactly the
   * idempotent second pass `applyChain` makes on every read.
   */
  const rejectedStored = stages.some((s) => s.status === 'rejected');
  if (!rejectedStored && stages.some((s) => s.status === 'cancelled'))
    return { status: 'cancelled', openStage: null, changes: [] };

  let openStage: number | null = null;
  let decided: 'approved' | 'rejected' | null = null;

  for (const s of stages) {
    if (decided) {
      // Everything after a decision is moot. Cancelled, not left waiting —
      // see the doc comment.
      want(s, 'cancelled');
      continue;
    }
    if (openStage !== null) {
      // A stage after the open one has not been reached yet.
      want(s, 'waiting');
      continue;
    }
    const outcome = stageOutcome(s.mode, s.responses);
    if (outcome === 'approved') {
      want(s, 'approved');
    } else if (outcome === 'rejected') {
      want(s, 'rejected');
      decided = 'rejected';
    } else {
      want(s, 'pending');
      openStage = s.stage;
    }
  }

  if (decided === 'rejected') return { status: 'rejected', openStage: null, changes };
  if (openStage === null) return { status: 'approved', openStage: null, changes };
  return { status: 'pending', openStage, changes };
}

/**
 * Who is actually being asked, once out-of-office delegation is applied.
 *
 * Returns the delegate where one is active, and says who it is standing in
 * for — an approval recorded with no trace of whose authority it used is a
 * hole in the audit trail, and "why did Sam approve my £40k change?" needs an
 * answer better than "they were covering".
 *
 * A delegation chain is NOT followed. If A delegates to B and B delegates to
 * C, the request goes to B: following it would let two people who never spoke
 * hand an approval to a third, and a cycle would hang the resolver.
 */
export function resolveApprover(
  userId: string,
  delegations: { fromUserId: string; toUserId: string; startsAt: number; endsAt: number | null }[],
  now: number,
): { userId: string; onBehalfOf: string | null } {
  const active = delegations.find(
    (d) => d.fromUserId === userId && d.startsAt <= now && (d.endsAt === null || d.endsAt > now),
  );
  if (!active || active.toUserId === userId) return { userId, onBehalfOf: null };
  return { userId: active.toUserId, onBehalfOf: userId };
}

/**
 * Where a THING stands on approval, across every chain raised against it.
 *
 * Step 193. The change gate used to ask a boolean — "has a chain finished
 * with approved?" — which has no way to say *nothing was ever asked of
 * anyone*. A change with no CAB and no approval request therefore read as
 * "not approved" and sat in Awaiting approval forever, with a refusal
 * message that named a decision nobody had been invited to make.
 *
 * Kai, 2026-09-20: *"Change is now stuck on approval because there's no cab,
 * if there's no cab it should just not need approving."*
 *
 * `none` is the answer that boolean could not give, and it is the common one:
 * approval is opt-in per change until somebody requests it.
 */
export type ApprovalState = 'none' | 'pending' | 'approved' | 'rejected';

/**
 * Roll several chains up into one answer.
 *
 * Precedence is deliberate: an approval WINS over a rejection. Asking a
 * second time after a refusal — with the plan revised, or a different board —
 * is the normal way a change gets over the line, and the newer decision is
 * the real one. A cancelled chain is withdrawn, so it counts for nothing;
 * when every chain is cancelled the answer is `none`, exactly as if none had
 * been raised, because that is what cancelling them meant.
 */
export function approvalState(chainStatuses: readonly string[]): ApprovalState {
  const live = chainStatuses.filter((s) => s !== 'cancelled');
  if (!live.length) return 'none';
  if (live.includes('approved')) return 'approved';
  if (live.includes('pending')) return 'pending';
  return 'rejected';
}
